import { Box, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { COLORS } from "../../Styles/Colors";

interface DatePickerProps {
  startDate: Date | null;
  setStartDate: (date: Date | null) => void;
  endDate: Date | null;
  setEndDate: (date: Date | null) => void;
  colorLabel?: string;
  dropdownColor?: string;
}

const isValidDate = (date: Date | null): boolean =>
  date !== null && !isNaN(date.getTime());

const fieldSx = (dropdownColor: string) => ({
  backgroundColor: dropdownColor,
  width: '100%',
  minWidth: 0,
  '& .MuiInputBase-root': {
    fontSize: { xs: '0.8125rem', sm: '1rem' },
  },
  '& .MuiInputBase-input': {
    py: { xs: 0.625, sm: 1 },
  },
  '& .MuiIconButton-root': {
    padding: { xs: '4px', sm: '8px' },
  },
});

export const DateRangePicker = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  colorLabel = COLORS.darkGray,
  dropdownColor = COLORS.white,
}: DatePickerProps) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="flex-end"
        gap={{ xs: 1, sm: 2, md: 3 }}
        mb={0}
        mt={0}
        sx={{ width: { xs: '100%', sm: 'auto' }, maxWidth: '100%', minWidth: 0 }}
      >
        <Box flex={1} minWidth={0}>
          <Typography
            component="label"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: colorLabel,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              lineHeight: 1.2,
              mb: { xs: 0.25, sm: 0.5 },
            }}
          >
            Start Date
          </Typography>
          <DatePicker
            value={startDate}
            sx={fieldSx(dropdownColor)}
            onChange={(newValue) => {
              if (newValue === null || isValidDate(newValue)) {
                setStartDate(newValue);
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                variant: 'outlined',
              },
              field: { clearable: true },
            }}
          />
        </Box>

        <Box flex={1} minWidth={0}>
          <Typography
            component="label"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: colorLabel,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              lineHeight: 1.2,
              mb: { xs: 0.25, sm: 0.5 },
            }}
          >
            End Date
          </Typography>
          <DatePicker
            value={endDate}
            sx={fieldSx(dropdownColor)}
            onChange={(newValue) => {
              if (newValue === null || isValidDate(newValue)) {
                setEndDate(newValue);
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                variant: 'outlined',
              },
              field: { clearable: true },
            }}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
